import { apiFetch } from './ApiService.js';
import type { IToastService } from './ToastService.js';

/// Login + logout via JS fetch — ingen `<form method="post">`.
///
/// Login: læser felter fra `[data-form="login"]`-scope, POSTer til
/// `/api/account/login`. Server sætter cookie via Set-Cookie og returnerer
/// `{ ok, redirect, detail }`. Ved success navigerer vi til `redirect`.
///
/// Logout: POSTer til `/api/account/logout`, navigerer til `/Account/Login`.

interface LoginResponse {
  ok: boolean;
  detail?: string | null;
  redirect?: string | null;
}

export interface IAuthService {
  login(): Promise<void>;
  logout(): Promise<void>;
}

export class AuthService implements IAuthService {
  constructor(
    private readonly toast: IToastService,
    private readonly doc: Document = document,
  ) { }

  async login(): Promise<void> {
    const scope = this.doc.querySelector<HTMLElement>('[data-form="login"]');
    if (!scope) {
      console.warn('AuthService.login: ingen [data-form="login"]-scope fundet');
      return;
    }

    const email = this.readInput(scope, 'email');
    const password = this.readInput(scope, 'password');
    const rememberMe = this.readCheckbox(scope, 'rememberMe');
    const returnUrl = scope.getAttribute('data-return-url') ?? '';

    this.clearFormError(scope);
    if (!email || !password) {
      this.showFormError(scope, 'Email og password er påkrævet');
      return;
    }

    try {
      const resp = await apiFetch('/api/account/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email, password, rememberMe, returnUrl }),
      });

      const body = await this.readBody(resp);
      if (resp.ok && body.ok) {
        window.location.href = body.redirect ?? '/';
        return;
      }

      this.showFormError(scope, body.detail ?? `HTTP ${resp.status}`);
    } catch (e) {
      console.error(e);
      this.showFormError(scope, 'Netværksfejl — kunne ikke nå serveren');
    }
  }

  async logout(): Promise<void> {
    try {
      await apiFetch('/api/account/logout', {
        method: 'POST',
        credentials: 'same-origin',
      });
    } catch (e) {
      console.error(e);
      this.toast.show('Logout fejlede — siden genindlæses', 'error');
    } finally {
      window.location.href = '/Account/Login';
    }
  }

  private readInput(scope: HTMLElement, name: string): string {
    const el = scope.querySelector<HTMLInputElement>(`[name="${name}"]`);
    return (el?.value ?? '').trim();
  }

  private readCheckbox(scope: HTMLElement, name: string): boolean {
    const el = scope.querySelector<HTMLInputElement>(`[name="${name}"]`);
    return el?.checked ?? false;
  }

  private showFormError(scope: HTMLElement, msg: string): void {
    const banner = scope.querySelector<HTMLElement>('[data-field-error="form"]');
    if (!banner) return;
    const span = banner.querySelector('span');
    if (span) span.textContent = msg;
    banner.hidden = false;
  }

  private clearFormError(scope: HTMLElement): void {
    const banner = scope.querySelector<HTMLElement>('[data-field-error="form"]');
    if (!banner) return;
    banner.hidden = true;
    const span = banner.querySelector('span');
    if (span) span.textContent = '';
  }

  private async readBody(resp: Response): Promise<LoginResponse> {
    try {
      return await resp.json() as LoginResponse;
    } catch {
      return { ok: false, detail: `HTTP ${resp.status}` };
    }
  }
}
